import React, { useState } from 'react';
import {
  Box, Typography, Chip, Alert, Button, Paper, Table, TableBody, TableRow, TableCell,
  TextField, MenuItem, Snackbar, CircularProgress,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { saveApplicationForm } from '../../services/studentService';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const has = (v) => v != null && v !== '';

const MARITAL_OPTIONS = ['', 'Unmarried', 'Married', 'Divorced', 'Other'];
const EMPLOYMENT_OPTIONS = ['', 'Salaried', 'Self Employed', 'Internship', 'Freelance', 'Currently Studying', 'Other'];
const YES_NO = ['', 'Yes', 'No'];
const YES_NO_MAYBE = ['', 'Yes', 'No', 'Maybe'];

const Field = ({ label, value }) => (
  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' } }}>
    <TableCell sx={{ width: 220, py: 1.25, px: 2, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
      {label}
    </TableCell>
    <TableCell sx={{ py: 1.25, px: 2, border: '1px solid #E2E8F0', fontSize: '0.85rem', fontWeight: 500, color: has(value) ? '#1E293B' : '#94A3B8', fontStyle: has(value) ? 'normal' : 'italic', wordBreak: 'break-word' }}>
      {has(value) ? String(value) : 'Not provided'}
    </TableCell>
  </TableRow>
);

const FieldRow = ({ fields }) => (
  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' } }}>
    {fields.map(({ label, value }, i) => (
      <React.Fragment key={i}>
        <TableCell sx={{ py: 1.25, px: 2, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
          {label}
        </TableCell>
        <TableCell sx={{ py: 1.25, px: 2, border: '1px solid #E2E8F0', fontSize: '0.85rem', fontWeight: 500, color: has(value) ? '#1E293B' : '#94A3B8', fontStyle: has(value) ? 'normal' : 'italic', wordBreak: 'break-word' }}>
          {has(value) ? String(value) : 'Not provided'}
        </TableCell>
      </React.Fragment>
    ))}
  </TableRow>
);

const SectionHeader = ({ number, title, icon, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, backgroundColor: color, borderRadius: '8px 8px 0 0' }}>
    <Box sx={{ color: '#fff', display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
      Section {number} — {title}
    </Typography>
  </Box>
);

const SubHead = ({ label }) => (
  <Box sx={{ backgroundColor: '#F1F5F9', px: 2, py: 0.75, border: '1px solid #E2E8F0' }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </Typography>
  </Box>
);

// ─── Edit Field Components ───────────────────────────────────────────────────
const EditField = ({ label, value, onChange, multiline }) => (
  <TableRow>
    <TableCell sx={{ width: 220, py: 1, px: 2, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
      {label}
    </TableCell>
    <TableCell sx={{ py: 0.5, px: 1.5, border: '1px solid #E2E8F0' }}>
      <TextField fullWidth size="small" value={value || ''} onChange={(e) => onChange(e.target.value)} multiline={multiline} minRows={multiline ? 2 : 1} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
    </TableCell>
  </TableRow>
);

const EditFieldRow = ({ fields }) => (
  <TableRow>
    {fields.map(({ label, value, onChange, select, options }, i) => (
      <React.Fragment key={i}>
        <TableCell sx={{ py: 1, px: 2, border: '1px solid #E2E8F0', fontWeight: 600, fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
          {label}
        </TableCell>
        <TableCell sx={{ py: 0.5, px: 1.5, border: '1px solid #E2E8F0' }}>
          {select ? (
            <TextField fullWidth select size="small" value={value || ''} onChange={(e) => onChange(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}>
              {options.map((opt) => <MenuItem key={opt} value={opt}>{opt || '— Select —'}</MenuItem>)}
            </TextField>
          ) : (
            <TextField fullWidth size="small" value={value || ''} onChange={(e) => onChange(e.target.value)} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
          )}
        </TableCell>
      </React.Fragment>
    ))}
  </TableRow>
);

// ─── PDF Download ────────────────────────────────────────────────────────────
const downloadPDF = (studentName, fileNumber, formData, student) => {
  const { s1 = {}, s2 = {}, s3 = {}, s4 = {}, s5 = {} } = formData;
  const esc = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const row = (label, value) => { const v = has(value) ? esc(value) : '<span class="empty">Not provided</span>'; return `<tr><td class="label">${esc(label)}</td><td>${v}</td></tr>`; };
  const row2 = (l1, v1, l2, v2) => { const a = has(v1) ? esc(v1) : '<span class="empty">Not provided</span>'; const b = has(v2) ? esc(v2) : '<span class="empty">Not provided</span>'; return `<tr><td class="label">${esc(l1)}</td><td>${a}</td><td class="label">${esc(l2)}</td><td>${b}</td></tr>`; };
  const sh = (num, title, cls) => `<div class="section-header ${cls}">Section ${num} — ${title}</div>`;
  const sub = (label) => `<div class="sub-head">${label}</div>`;
  const title = `Loan Application - ${esc(studentName || 'Student')}${fileNumber ? ` (${esc(fileNumber)})` : ''}`;
  const now = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const html = `<!DOCTYPE html><html><head><title>${title}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;color:#1E293B;padding:28px 32px;font-size:12.5px;-webkit-print-color-adjust:exact;print-color-adjust:exact}h1{font-size:20px;font-weight:700;color:#1E3A5F;margin-bottom:2px}.meta{font-size:11px;color:#5F6368;margin-bottom:20px}.section-header{color:#fff;padding:9px 16px;font-weight:700;font-size:13px;border-radius:6px 6px 0 0;margin-top:18px}.section-header.blue{background:#2C7BE5}.section-header.purple{background:#7C3AED}.section-header.green{background:#059669}.section-header.amber{background:#D97706}.section-header.gray{background:#475569}.sub-head{background:#F1F5F9;padding:5px 16px;font-weight:700;font-size:11px;color:#475569;text-transform:uppercase;letter-spacing:0.5px;border:1px solid #E2E8F0;border-top:none}table{width:100%;border-collapse:collapse}td{padding:7px 16px;border:1px solid #E2E8F0;font-size:12.5px;vertical-align:top}td.label{font-weight:600;color:#475569;white-space:nowrap;width:22%}tr:nth-child(odd) td{background:#F8FAFC}.empty{color:#94A3B8;font-style:italic}@media print{body{padding:16px 20px}@page{margin:12mm}}</style></head><body><h1>${title}</h1><div class="meta">Generated on ${now}</div>${sh(1,'Student Details','blue')}${sub('1.1 Basic Information')}<table>${row2('Full Name',student?.name,'Email',student?.email)}${row2('Phone',student?.phone,'Marital Status',s1.maritalStatus)}</table>${sub('1.2 Address')}<table>${row('Current Address',s1.currentAddress)}${row('Years at Current Address',s1.currentAddressYears)}${row('Permanent Address',s1.permanentAddress)}</table>${sub('1.3 Academic')}<table>${row2('10th Score (%)',s1.tenthScore,'12th Score (%)',s1.twelfthScore)}${row2("Bachelor's Score (%)",s1.bachelorsScore,'Backlogs',s1.graduationBacklogs)}</table>${sub('1.4 Tests')}<table>${row2('Language Test',s1.languageTest,'GRE/GMAT',s1.greGmatScore)}</table>${sub('1.5 Reference')}<table>${row2('Ref Mobile',s1.refPhone,'Ref Address',s1.refAddress)}</table>${sub('1.6 Work')}<table>${row2('Employment',s1.employmentType,'Experience',s1.workExperience)}${row('Company',s1.companyAddress)}</table>${sh(2,'University','purple')}<table>${row2('Country',s2.targetCountry,'University',s2.universityName)}${row2('Course',s2.courseName,'Duration',s2.courseDuration)}${row('Loan Amount',s2.loanAmount?'₹'+s2.loanAmount+' Lakhs':null)}</table>${sh(3,'Parents','green')}${sub("Father")}<table>${row2("Father's Name",s3.fatherName,"Contact",s3.fatherPhone)}${row("Email",s3.fatherEmail)}</table>${sub("Mother")}<table>${row2("Mother's Name",s3.motherName,"Contact",s3.motherPhone)}${row("Email",s3.motherEmail)}</table>${sub("Residence")}<table>${row("Address",s3.residenceAddress)}</table>${sh(4,'Co-Applicant','amber')}${sub('Basic')}<table>${row2('Name',s4.name,'Contact',s4.phone)}${row2('Email',s4.email,'Relationship',s4.relationship)}${row('Address',s4.address)}</table>${sub('Employment')}<table>${row2('Occupation',s4.occupation,'Years',s4.yearsInJob)}${row('Office',s4.officeAddress)}${row2('Income (ITR)',s4.annualIncome?'₹'+s4.annualIncome+'L':null,'EMIs',s4.monthlyEmis?'₹'+s4.monthlyEmis:null)}</table>${sub('Family')}<table>${row2("Father",s4.fatherName,"Mother",s4.motherName)}</table>${sub('References')}<table>${row2('Ref1',s4.ref1Name,'Mobile',s4.ref1Phone)}${row2('Email',s4.ref1Email,'Address',s4.ref1Address)}${row2('Ref2',s4.ref2Name,'Mobile',s4.ref2Phone)}${row2('Email',s4.ref2Email,'Address',s4.ref2Address)}</table>${sh(5,'Miscellaneous','gray')}<table>${row('Previous Banks',s5.previousBanks)}${row2('Own House',s5.ownHouse,'Collateral',s5.collateral)}${s5.collateralTypes?.length?row('Collateral Types',s5.collateralTypes.join(', ')):''}</table><script>setTimeout(function(){window.print()},300)</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ApplicationFormView = ({ applicationForm, student, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultForm = {
    s1: { maritalStatus: '', currentAddress: '', currentAddressYears: '', permanentAddress: '', tenthScore: '', twelfthScore: '', bachelorsScore: '', graduationBacklogs: '', languageTest: '', greGmatScore: '', refPhone: '', refAddress: '', employmentType: '', workExperience: '', companyAddress: '' },
    s2: { targetCountry: '', universityName: '', courseName: '', courseDuration: '', loanAmount: '' },
    s3: { fatherName: '', fatherPhone: '', fatherEmail: '', motherName: '', motherPhone: '', motherEmail: '', residenceAddress: '' },
    s4: { name: '', phone: '', email: '', address: '', relationship: '', occupation: '', officeAddress: '', yearsInJob: '', annualIncome: '', monthlyEmis: '', fatherName: '', motherName: '', ref1Name: '', ref1Phone: '', ref1Email: '', ref1Address: '', ref2Name: '', ref2Phone: '', ref2Email: '', ref2Address: '', hasAdditional: '' },
    s5: { previousBanks: '', ownHouse: '', collateral: '', collateralTypes: [] },
  };

  const [form, setForm] = useState(() => ({
    s1: { ...defaultForm.s1, ...(applicationForm?.s1 || {}) },
    s2: { ...defaultForm.s2, ...(applicationForm?.s2 || {}) },
    s3: { ...defaultForm.s3, ...(applicationForm?.s3 || {}) },
    s4: { ...defaultForm.s4, ...(applicationForm?.s4 || {}) },
    s5: { ...defaultForm.s5, ...(applicationForm?.s5 || {}) },
  }));

  const set = (section, field) => (value) => {
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      await saveApplicationForm(student._id || student.id, {
        ...form,
        status: submit ? 'submitted' : 'draft',
        declaration: submit,
      });
      setSnackbar({ open: true, message: submit ? 'Application submitted!' : 'Application saved!', severity: 'success' });
      setEditing(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = () => {
    setForm({
      s1: { ...defaultForm.s1, ...(applicationForm?.s1 || {}) },
      s2: { ...defaultForm.s2, ...(applicationForm?.s2 || {}) },
      s3: { ...defaultForm.s3, ...(applicationForm?.s3 || {}) },
      s4: { ...defaultForm.s4, ...(applicationForm?.s4 || {}) },
      s5: { ...defaultForm.s5, ...(applicationForm?.s5 || {}) },
    });
    setEditing(true);
  };

  // No form exists yet - show create button
  if (!applicationForm && !editing) {
    return (
      <Box>
        <Alert severity="info" icon={<AssignmentIcon />} sx={{ borderRadius: 2, alignItems: 'center', mb: 2 }}>
          <Typography fontWeight={600} fontSize="0.875rem">Loan Application Form Not Submitted</Typography>
          <Typography fontSize="0.8rem" sx={{ mt: 0.25, color: 'text.secondary' }}>
            No application form exists yet. You can create one now.
          </Typography>
        </Alert>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Create Loan Application
        </Button>
      </Box>
    );
  }

  const data = editing ? form : {
    s1: applicationForm?.s1 || {},
    s2: applicationForm?.s2 || {},
    s3: applicationForm?.s3 || {},
    s4: applicationForm?.s4 || {},
    s5: applicationForm?.s5 || {},
  };

  const { s1, s2, s3, s4, s5 } = data;
  const isSubmitted = applicationForm?.status === 'submitted';
  const formattedDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <Box>
      {/* Header bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {applicationForm && (
            <Chip label={isSubmitted ? 'Submitted' : 'Draft'} color={isSubmitted ? 'success' : 'warning'} size="small" sx={{ fontWeight: 700 }} />
          )}
          {isSubmitted && applicationForm?.submittedAt && (
            <Typography variant="caption" color="text.secondary">Submitted on {formattedDate(applicationForm.submittedAt)}</Typography>
          )}
          {!isSubmitted && applicationForm?.updatedAt && (
            <Typography variant="caption" color="text.secondary">Last saved {formattedDate(applicationForm.updatedAt)}</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {editing ? (
            <>
              <Button variant="outlined" size="small" startIcon={<CloseIcon />} onClick={() => setEditing(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Cancel
              </Button>
              <Button variant="outlined" size="small" color="warning" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={() => handleSave(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Save Draft
              </Button>
              <Button variant="contained" size="small" color="success" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={() => handleSave(true)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Submit
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleStartEdit} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Edit Form
              </Button>
              {applicationForm && (
                <Button variant="contained" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => downloadPDF(student?.name, student?.fileNumber, applicationForm, student)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Download PDF
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Section 1: Student Details */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader number={1} title="Student Details" icon={<PersonIcon />} color="#2C7BE5" />
        <SubHead label="1.1 Basic Information" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[
                { label: 'Full Name', value: student?.name, onChange: () => {} },
                { label: 'Marital Status', value: s1.maritalStatus, onChange: set('s1', 'maritalStatus'), select: true, options: MARITAL_OPTIONS },
              ]} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: 'Full Name', value: student?.name }, { label: 'Email', value: student?.email }]} />
              <FieldRow fields={[{ label: 'Phone', value: student?.phone }, { label: 'Marital Status', value: s1.maritalStatus }]} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="1.2 Address Information" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditField label="Current Address (with Pin Code)" value={s1.currentAddress} onChange={set('s1', 'currentAddress')} multiline />
              <EditField label="Years at Current Address" value={s1.currentAddressYears} onChange={set('s1', 'currentAddressYears')} />
              <EditField label="Permanent Address (with Pin Code)" value={s1.permanentAddress} onChange={set('s1', 'permanentAddress')} multiline />
            </>
          ) : (
            <>
              <Field label="Current Address (with Pin Code)" value={s1.currentAddress} />
              <Field label="Years at Current Address" value={s1.currentAddressYears} />
              <Field label="Permanent Address (with Pin Code)" value={s1.permanentAddress} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="1.3 Academic Information" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: '10th Score (%)', value: s1.tenthScore, onChange: set('s1', 'tenthScore') }, { label: '12th Score (%)', value: s1.twelfthScore, onChange: set('s1', 'twelfthScore') }]} />
              <EditFieldRow fields={[{ label: "Bachelor's Score (%)", value: s1.bachelorsScore, onChange: set('s1', 'bachelorsScore') }, { label: 'Graduation Backlogs', value: s1.graduationBacklogs, onChange: set('s1', 'graduationBacklogs') }]} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: '10th Score (%)', value: s1.tenthScore }, { label: '12th Score (%)', value: s1.twelfthScore }]} />
              <FieldRow fields={[{ label: "Bachelor's Score (%)", value: s1.bachelorsScore }, { label: 'Graduation Backlogs', value: s1.graduationBacklogs }]} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="1.4 Test Scores" />
        <Table><TableBody>
          {editing ? (
            <EditFieldRow fields={[{ label: 'Language Test + Score', value: s1.languageTest, onChange: set('s1', 'languageTest') }, { label: 'GRE / GMAT Score', value: s1.greGmatScore, onChange: set('s1', 'greGmatScore') }]} />
          ) : (
            <FieldRow fields={[{ label: 'Language Test + Score', value: s1.languageTest }, { label: 'GRE / GMAT Score', value: s1.greGmatScore }]} />
          )}
        </TableBody></Table>
        <SubHead label="1.5 Reference Details" />
        <Table><TableBody>
          {editing ? (
            <EditFieldRow fields={[{ label: 'Reference Mobile', value: s1.refPhone, onChange: set('s1', 'refPhone') }, { label: 'Reference Address', value: s1.refAddress, onChange: set('s1', 'refAddress') }]} />
          ) : (
            <FieldRow fields={[{ label: 'Reference Mobile', value: s1.refPhone }, { label: 'Reference Address', value: s1.refAddress }]} />
          )}
        </TableBody></Table>
        <SubHead label="1.6 Work Profile" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: 'Employment Type', value: s1.employmentType, onChange: set('s1', 'employmentType'), select: true, options: EMPLOYMENT_OPTIONS }, { label: 'Experience (Years)', value: s1.workExperience, onChange: set('s1', 'workExperience') }]} />
              <EditField label="Company Name & Address" value={s1.companyAddress} onChange={set('s1', 'companyAddress')} multiline />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: 'Employment Type', value: s1.employmentType }, { label: 'Experience (Years)', value: s1.workExperience }]} />
              <Field label="Company Name & Address" value={s1.companyAddress} />
            </>
          )}
        </TableBody></Table>
      </Paper>

      {/* Section 2: University Details */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader number={2} title="University Details" icon={<SchoolIcon />} color="#7C3AED" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: 'Target Country', value: s2.targetCountry, onChange: set('s2', 'targetCountry') }, { label: 'University Name', value: s2.universityName, onChange: set('s2', 'universityName') }]} />
              <EditFieldRow fields={[{ label: 'Course Name', value: s2.courseName, onChange: set('s2', 'courseName') }, { label: 'Course Duration', value: s2.courseDuration, onChange: set('s2', 'courseDuration') }]} />
              <EditField label="Loan Amount (Lakhs)" value={s2.loanAmount} onChange={set('s2', 'loanAmount')} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: 'Target Country', value: s2.targetCountry }, { label: 'University Name', value: s2.universityName }]} />
              <FieldRow fields={[{ label: 'Course Name', value: s2.courseName }, { label: 'Course Duration', value: s2.courseDuration }]} />
              <Field label="Loan Amount Required" value={s2.loanAmount ? `₹${s2.loanAmount} Lakhs` : null} />
            </>
          )}
        </TableBody></Table>
      </Paper>

      {/* Section 3: Parents Details */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader number={3} title="Parents Details" icon={<FamilyRestroomIcon />} color="#059669" />
        <SubHead label="Father's Details" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: "Father's Name", value: s3.fatherName, onChange: set('s3', 'fatherName') }, { label: "Father's Contact", value: s3.fatherPhone, onChange: set('s3', 'fatherPhone') }]} />
              <EditField label="Father's Email" value={s3.fatherEmail} onChange={set('s3', 'fatherEmail')} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: "Father's Name", value: s3.fatherName }, { label: "Father's Contact", value: s3.fatherPhone }]} />
              <Field label="Father's Email" value={s3.fatherEmail} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="Mother's Details" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: "Mother's Name", value: s3.motherName, onChange: set('s3', 'motherName') }, { label: "Mother's Contact", value: s3.motherPhone, onChange: set('s3', 'motherPhone') }]} />
              <EditField label="Mother's Email" value={s3.motherEmail} onChange={set('s3', 'motherEmail')} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: "Mother's Name", value: s3.motherName }, { label: "Mother's Contact", value: s3.motherPhone }]} />
              <Field label="Mother's Email" value={s3.motherEmail} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="Residence Address" />
        <Table><TableBody>
          {editing ? (
            <EditField label="Residence Address (with Pin Code)" value={s3.residenceAddress} onChange={set('s3', 'residenceAddress')} multiline />
          ) : (
            <Field label="Residence Address (with Pin Code)" value={s3.residenceAddress} />
          )}
        </TableBody></Table>
      </Paper>

      {/* Section 4: Co-Applicant */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader number={4} title="Co-Applicant Details" icon={<AccountBalanceIcon />} color="#D97706" />
        <SubHead label="4.1 Basic Details" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: 'Co-Applicant Name', value: s4.name, onChange: set('s4', 'name') }, { label: 'Contact Number', value: s4.phone, onChange: set('s4', 'phone') }]} />
              <EditFieldRow fields={[{ label: 'Email ID', value: s4.email, onChange: set('s4', 'email') }, { label: 'Relationship', value: s4.relationship, onChange: set('s4', 'relationship') }]} />
              <EditField label="Address (with Pin Code)" value={s4.address} onChange={set('s4', 'address')} multiline />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: 'Co-Applicant Name', value: s4.name }, { label: 'Contact Number', value: s4.phone }]} />
              <FieldRow fields={[{ label: 'Email ID', value: s4.email }, { label: 'Relationship', value: s4.relationship }]} />
              <Field label="Address (with Pin Code)" value={s4.address} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="4.2 Employment Details" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: 'Occupation', value: s4.occupation, onChange: set('s4', 'occupation'), select: true, options: EMPLOYMENT_OPTIONS }, { label: 'Years in Current Job', value: s4.yearsInJob, onChange: set('s4', 'yearsInJob') }]} />
              <EditField label="Office Name & Address" value={s4.officeAddress} onChange={set('s4', 'officeAddress')} multiline />
              <EditFieldRow fields={[{ label: 'Annual Income (ITR) in Lakhs', value: s4.annualIncome, onChange: set('s4', 'annualIncome') }, { label: 'Monthly EMIs', value: s4.monthlyEmis, onChange: set('s4', 'monthlyEmis') }]} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: 'Occupation', value: s4.occupation }, { label: 'Years in Current Job', value: s4.yearsInJob }]} />
              <Field label="Office Name & Address" value={s4.officeAddress} />
              <FieldRow fields={[{ label: 'Annual Income (ITR)', value: s4.annualIncome ? `₹${s4.annualIncome} L` : null }, { label: 'Monthly EMIs', value: s4.monthlyEmis ? `₹${s4.monthlyEmis}` : null }]} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="4.3 Family Details" />
        <Table><TableBody>
          {editing ? (
            <EditFieldRow fields={[{ label: "Father's Name", value: s4.fatherName, onChange: set('s4', 'fatherName') }, { label: "Mother's Name", value: s4.motherName, onChange: set('s4', 'motherName') }]} />
          ) : (
            <FieldRow fields={[{ label: "Father's Name", value: s4.fatherName }, { label: "Mother's Name", value: s4.motherName }]} />
          )}
        </TableBody></Table>
        <SubHead label="4.4 References" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditFieldRow fields={[{ label: 'Ref 1 — Name', value: s4.ref1Name, onChange: set('s4', 'ref1Name') }, { label: 'Ref 1 — Mobile', value: s4.ref1Phone, onChange: set('s4', 'ref1Phone') }]} />
              <EditFieldRow fields={[{ label: 'Ref 1 — Email', value: s4.ref1Email, onChange: set('s4', 'ref1Email') }, { label: 'Ref 1 — Address', value: s4.ref1Address, onChange: set('s4', 'ref1Address') }]} />
              <EditFieldRow fields={[{ label: 'Ref 2 — Name', value: s4.ref2Name, onChange: set('s4', 'ref2Name') }, { label: 'Ref 2 — Mobile', value: s4.ref2Phone, onChange: set('s4', 'ref2Phone') }]} />
              <EditFieldRow fields={[{ label: 'Ref 2 — Email', value: s4.ref2Email, onChange: set('s4', 'ref2Email') }, { label: 'Ref 2 — Address', value: s4.ref2Address, onChange: set('s4', 'ref2Address') }]} />
            </>
          ) : (
            <>
              <FieldRow fields={[{ label: 'Ref 1 — Name', value: s4.ref1Name }, { label: 'Ref 1 — Mobile', value: s4.ref1Phone }]} />
              <FieldRow fields={[{ label: 'Ref 1 — Email', value: s4.ref1Email }, { label: 'Ref 1 — Address', value: s4.ref1Address }]} />
              <FieldRow fields={[{ label: 'Ref 2 — Name', value: s4.ref2Name }, { label: 'Ref 2 — Mobile', value: s4.ref2Phone }]} />
              <FieldRow fields={[{ label: 'Ref 2 — Email', value: s4.ref2Email }, { label: 'Ref 2 — Address', value: s4.ref2Address }]} />
            </>
          )}
        </TableBody></Table>
        <SubHead label="4.5 Additional Co-Applicant" />
        <Table><TableBody>
          {editing ? (
            <EditFieldRow fields={[{ label: 'Has Additional Co-Applicant?', value: s4.hasAdditional, onChange: set('s4', 'hasAdditional'), select: true, options: YES_NO }]} />
          ) : (
            <Field label="Has Additional Co-Applicant?" value={s4.hasAdditional} />
          )}
        </TableBody></Table>
      </Paper>

      {/* Section 5: Miscellaneous */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
        <SectionHeader number={5} title="Miscellaneous" icon={<InfoOutlinedIcon />} color="#475569" />
        <Table><TableBody>
          {editing ? (
            <>
              <EditField label="Previously Applied Banks" value={s5.previousBanks} onChange={set('s5', 'previousBanks')} />
              <EditFieldRow fields={[
                { label: 'Own House Available?', value: s5.ownHouse, onChange: set('s5', 'ownHouse'), select: true, options: YES_NO },
                { label: 'Ready to Provide Collateral?', value: s5.collateral, onChange: set('s5', 'collateral'), select: true, options: YES_NO_MAYBE },
              ]} />
            </>
          ) : (
            <>
              <Field label="Previously Applied Banks" value={s5.previousBanks} />
              <FieldRow fields={[{ label: 'Own House Available?', value: s5.ownHouse }, { label: 'Ready to Provide Collateral?', value: s5.collateral }]} />
              {s5.collateralTypes?.length > 0 && (
                <Field label="Accepted Collateral Types" value={s5.collateralTypes.join(', ')} />
              )}
            </>
          )}
        </TableBody></Table>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationFormView;
